import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, KeyRound, Plus, RefreshCw } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { supabase } from '@/lib/supabase';
import {
  createAdminTechnician,
  listAdminTechnicians,
  setAdminTechnicianPassword,
  TechnicianProfile,
  updateAdminTechnician,
} from '@/lib/tech-api-client';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

export default function TechAdminPage() {
  const { accessToken } = useTechAuth();

  const getAccessToken = async (): Promise<string> => {
    if (accessToken) return accessToken;

    const { data, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr) throw sessionErr;

    const token = data.session?.access_token;
    if (!token) throw new Error('Missing auth token. Please sign in again.');
    return token;
  };

  const [rows, setRows] = useState<TechnicianProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const [passwordTech, setPasswordTech] = useState<TechnicianProfile | null>(null);
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordResult, setPasswordResult] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newRole, setNewRole] = useState<'technician' | 'lead_tech' | 'manager'>('technician');
  const [newEmployeeCode, setNewEmployeeCode] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const list = await listAdminTechnicians(token);
      setRows(list);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => String(a.full_name).localeCompare(String(b.full_name)));
  }, [rows]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    setCreating(true);
    setError(null);
    setTempPassword(null);

    try {
      const token = await getAccessToken();
      const res = await createAdminTechnician(token, {
        email: newEmail,
        full_name: newFullName,
        role: newRole,
        successware_employee_code: newEmployeeCode.trim() ? newEmployeeCode.trim() : null,
      });

      setRows((prev) => [res.technician, ...prev]);
      setTempPassword(res.tempPassword);
      setNewEmail('');
      setNewFullName('');
      setNewEmployeeCode('');
      setNewRole('technician');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (row: TechnicianProfile) => {
    setError(null);
    try {
      const token = await getAccessToken();
      const updated = await updateAdminTechnician(token, row.id, { is_active: !row.is_active });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const openPasswordModal = (row: TechnicianProfile) => {
    setError(null);
    setPasswordTech(row);
    setPasswordValue('');
    setPasswordResult(null);
  };

  const closePasswordModal = () => {
    if (passwordSubmitting) return;
    setPasswordTech(null);
    setPasswordValue('');
    setPasswordResult(null);
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordTech) return;

    setPasswordSubmitting(true);
    setError(null);
    setPasswordResult(null);

    try {
      const token = await getAccessToken();
      const password = passwordValue.trim() ? passwordValue.trim() : null;
      const temp = await setAdminTechnicianPassword(token, passwordTech.id, { password });
      setPasswordResult(temp);
      setPasswordValue('');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setPasswordSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
          <p className="text-gray-600 mt-1">Create and manage technician accounts.</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
            Refresh
          </button>

          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Create tech
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h2 className="text-lg font-semibold text-gray-900">Create technician</h2>

          <form onSubmit={handleCreate} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full name</label>
              <input
                value={newFullName}
                onChange={(e) => setNewFullName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                placeholder="Jane Technician"
                required
                disabled={creating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                placeholder="jane@company.com"
                required
                disabled={creating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as 'technician' | 'lead_tech' | 'manager')}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                disabled={creating}
              >
                <option value="technician">Technician</option>
                <option value="lead_tech">Lead tech</option>
                <option value="manager">Manager</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SuccessWare employee code (optional)</label>
              <input
                value={newEmployeeCode}
                onChange={(e) => setNewEmployeeCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                placeholder="1234"
                disabled={creating}
              />
            </div>

            <div className="md:col-span-2 flex items-center gap-3">
              <button
                type="submit"
                className="px-5 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                disabled={creating}
              >
                {creating ? 'Creating…' : 'Create account'}
              </button>

              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-5 py-2 rounded-xl bg-white border border-gray-300 hover:bg-gray-50"
                disabled={creating}
              >
                Cancel
              </button>
            </div>
          </form>

          {tempPassword && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-2xl">
              <div className="text-sm text-green-900 font-semibold">Temporary password</div>
              <div className="mt-1 font-mono text-lg text-green-900 break-all">{tempPassword}</div>
              <div className="mt-1 text-xs text-green-700">Give this password to the technician and have them change it.</div>
            </div>
          )}
        </div>
      )}

      {passwordTech && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Set / reset password</h2>
              <p className="text-sm text-gray-600 mt-1">
                Technician: <span className="font-medium text-gray-900">{passwordTech.full_name}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">Leave blank to generate a temp password.</p>
            </div>

            <button
              type="button"
              onClick={closePasswordModal}
              disabled={passwordSubmitting}
              className="px-4 py-2 rounded-xl bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
            >
              Close
            </button>
          </div>

          <form onSubmit={handleSetPassword} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">New password</label>
              <input
                value={passwordValue}
                onChange={(e) => setPasswordValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                placeholder="(leave blank to generate)"
                disabled={passwordSubmitting}
              />
            </div>

            <div className="md:col-span-2 flex items-center gap-3">
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                disabled={passwordSubmitting}
              >
                <KeyRound className="w-4 h-4" />
                {passwordSubmitting ? 'Updating…' : 'Set password'}
              </button>
            </div>
          </form>

          {passwordResult && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-2xl">
              <div className="text-sm text-green-900 font-semibold">New password</div>
              <div className="mt-1 font-mono text-lg text-green-900 break-all">{passwordResult}</div>
              <div className="mt-1 text-xs text-green-700">Share this password with the technician.</div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900">Technicians</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4">Active</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-gray-500">
                    No technicians found.
                  </td>
                </tr>
              ) : (
                sortedRows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-gray-900">{row.full_name}</div>
                      <div className="text-xs text-gray-500">{row.successware_employee_code || ''}</div>
                    </td>
                    <td className="py-3 pr-4">{row.email}</td>
                    <td className="py-3 pr-4">{row.role}</td>
                    <td className="py-3 pr-4">
                      <span
                        className={
                          row.is_active
                            ? 'inline-flex px-2 py-1 rounded-lg bg-green-100 text-green-800 text-xs'
                            : 'inline-flex px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-xs'
                        }
                      >
                        {row.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleActive(row)}
                          className="px-3 py-1 rounded-lg bg-white border border-gray-300 hover:bg-gray-50"
                        >
                          {row.is_active ? 'Disable' : 'Enable'}
                        </button>

                        <button
                          type="button"
                          onClick={() => openPasswordModal(row)}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-white border border-gray-300 hover:bg-gray-50"
                        >
                          <KeyRound className="w-4 h-4" />
                          Password
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
